"use client"
import {
  Grid,
  Typography,
  Box,
  Container,
  Paper,
  Chip,
  Card,
  CardContent,
  Avatar,
  useTheme,
  alpha,
} from "@mui/material"
import Status from "../../components/DataGrid/Status"
import { CardGiftcard, Person, Stars, ShoppingBasket, Receipt, AccessTime, CalendarToday } from "@mui/icons-material"

function OrderDetails({ data }) {
  const theme = useTheme()

  if (!data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          bgcolor: alpha(theme.palette.error.light, 0.1),
          borderRadius: 2,
          border: `1px dashed ${theme.palette.error.main}`,
        }}
      >
        <Typography color="error.main" variant="subtitle1">
          No order data to display
        </Typography>
      </Box>
    )
  }

  const { giftName, clientName, nbr_pts, quantity, status, orderDate = new Date().toISOString() } = data

  // Format the date
  const formattedDate = new Date(orderDate).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })

  // Format the time
  const formattedTime = new Date(orderDate).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  })

  const orderDetails = [
    {
      label: "Gift Name",
      value: giftName,
      icon: <CardGiftcard />,
      color: theme.palette.primary.main,
    },
    {
      label: "Client Name",
      value: clientName,
      icon: <Person />,
      color: theme.palette.secondary.main,
    },
    {
      label: "Points",
      value: nbr_pts,
      icon: <Stars />,
      color: theme.palette.warning.main,
    },
    {
      label: "Quantity",
      value: quantity,
      icon: <ShoppingBasket />,
      color: theme.palette.info.main,
    },
  ]

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: "absolute",
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: alpha("#fff", 0.1),
              top: -30,
              right: -30,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: alpha("#fff", 0.1),
              bottom: -20,
              left: 30,
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 2 }}>
            <Receipt sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Order Details
              </Typography>
              <Typography variant="body2">Complete information about this order</Typography>
            </Box>
          </Box>
        </Box>

        {/* Status Section */}
        <Box
          sx={{
            p: 3,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Order Status:
            </Typography>
            <Status value={status} />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formattedDate}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTime fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formattedTime}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Order Details Grid */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {orderDetails.map((detail, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 10px 20px ${alpha(detail.color, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(detail.color, 0.1),
                          color: detail.color,
                        }}
                      >
                        {detail.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {detail.label}
                        </Typography>
                        <Typography variant="h6" fontWeight="medium">
                          {detail.value}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Order ID: #ORD-
            {Math.floor(Math.random() * 10000)
              .toString()
              .padStart(4, "0")}
          </Typography>

          <Chip
            label={`Total Points: ${nbr_pts * quantity}`}
            color="primary"
            icon={<Stars fontSize="small" />}
            sx={{ fontWeight: "medium" }}
          />
        </Box>
      </Paper>
    </Container>
  )
}

export default OrderDetails

